import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import Modal from '../../components/Modal';
import './Enrollments.css';

const Enrollments = () => {
  const { t } = useTranslation();
  const { data, enrollStudent, bulkEnrollStudents, unenrollStudent, updateEnrollment, getCourseById } = useData();
  const { selectedLocation } = useOutletContext();
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showBulkEnrollModal, setShowBulkEnrollModal] = useState(false);
  const [enrollModalStudy, setEnrollModalStudy] = useState('');
  const [enrollStudentSearch, setEnrollStudentSearch] = useState('');
  const [bulkStudy, setBulkStudy] = useState('');
  const [bulkYear, setBulkYear] = useState('');
  const [bulkCohort, setBulkCohort] = useState('');
  const [bulkCourse, setBulkCourse] = useState('');
  const [bulkStudentSearch, setBulkStudentSearch] = useState('');
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [editingStatus, setEditingStatus] = useState('active');
  const [editingStudyYearMode, setEditingStudyYearMode] = useState('auto');
  const [editingCurrentStudyYear, setEditingCurrentStudyYear] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('');

  const periodOptions = [
    { value: 'all', label: 'Alles' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Maand' },
    { value: 'year', label: 'Jaar' }
  ];

  const studies = useMemo(() => data?.studies || [], [data?.studies]);
  const courses = useMemo(() => data?.courses || [], [data?.courses]);

  const getCohortDisplayName = (cohort) => {
    const name = String(cohort?.name || '').trim();
    const code = String(cohort?.code || '').trim();
    if (!name) return code;
    if (!code) return name;
    if (name.toLowerCase().includes(code.toLowerCase())) return name;
    return `${name} (${code})`;
  };

  const getStudyOptionLabel = (study) => {
    const cohortNames = (study.cohorts || []).map(getCohortDisplayName).filter(Boolean);
    return cohortNames.length > 0 ? `${study.name} ${cohortNames.join(', ')}` : study.name;
  };

  const courseMetadata = useMemo(() => {
    const entries = [];

    studies.forEach((study) => {
      const yearsById = new Map(
        (study.studyYears || []).map((year) => [String(year.id || year._id), year])
      );

      courses.forEach((course) => {
        const yearId = String(course.studyYear?.id || course.studyYear || '');
        if (!yearsById.has(yearId)) {
          return;
        }

        const year = yearsById.get(yearId);
        const linkedModules = Array.isArray(course.modules) && course.modules.length > 0
          ? course.modules
          : (course.module ? [course.module] : []);

        if (linkedModules.length === 0) {
          entries.push({
            id: String(course.id || course._id),
            studyId: String(study.id || study._id),
            studyName: study.name,
            studyLocation: study.location,
            yearId,
            yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
            yearNumber: year.yearNumber || year.year,
            moduleId: `course-${String(course.id || course._id)}-default`,
            moduleName: 'Module nog niet ingesteld',
            courseName: course.name,
            courseCode: course.code,
            courseLocation: course.location
          });
          return;
        }

        linkedModules.forEach((moduleDoc) => {
          const moduleId = String(moduleDoc?.id || moduleDoc?._id || moduleDoc || '');
          entries.push({
            id: String(course.id || course._id),
            studyId: String(study.id || study._id),
            studyName: study.name,
            studyLocation: study.location,
            yearId,
            yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
            yearNumber: year.yearNumber || year.year,
            moduleId,
            moduleName: moduleDoc?.name || moduleDoc?.code || 'Module',
            courseName: course.name,
            courseCode: course.code,
            courseLocation: course.location
          });
        });
      });
    });

    return entries;
  }, [courses, studies]);

  const courseMetadataById = useMemo(() => {
    const mapping = new Map();
    const ordered = [...courseMetadata].sort((a, b) => Number(a.yearNumber || 0) - Number(b.yearNumber || 0));
    ordered.forEach((entry) => {
      if (!mapping.has(entry.id)) {
        mapping.set(entry.id, entry);
      }
    });
    return mapping;
  }, [courseMetadata]);

  const studiesWithAssignments = useMemo(() => {
    const studyIds = new Set(courseMetadata.map((entry) => String(entry.studyId)));
    return studies.filter((study) => studyIds.has(String(study.id || study._id)));
  }, [courseMetadata, studies]);

  const allCourses = useMemo(() => {
    const deduped = new Map();

    courseMetadata.forEach((entry) => {
      if (deduped.has(entry.id)) {
        return;
      }

      const course = getCourseById(entry.id);
      deduped.set(entry.id, {
        ...(course || {}),
        id: entry.id,
        name: entry.courseName,
        code: entry.courseCode,
        moduleName: entry.moduleName,
        studyName: entry.studyName,
        yearLabel: entry.yearLabel,
        studyId: entry.studyId,
        yearId: entry.yearId
      });
    });

    return Array.from(deduped.values())
      .sort((left, right) => {
        const studyCompare = left.studyName.localeCompare(right.studyName, 'nl');
        if (studyCompare !== 0) return studyCompare;
        const yearCompare = String(left.yearLabel).localeCompare(String(right.yearLabel), 'nl');
        if (yearCompare !== 0) return yearCompare;
        return left.name.localeCompare(right.name, 'nl');
      });
  }, [courseMetadata, getCourseById]);

  const assignmentRows = useMemo(() => {
    return data.enrollments.map((enrollment) => {
      const course = getCourseById(enrollment.courseId);
      const student = data.students.find((item) => String(item.id) === String(enrollment.studentId));
      const metadata = courseMetadataById.get(String(enrollment.courseId)) || {};
      const fallbackStudyId = String(enrollment.studyId || '');
      const fallbackStudy = studies.find((item) => String(item.id || item._id) === fallbackStudyId);
      const courseModuleNames = (course?.modules || [])
        .map((moduleDoc) => moduleDoc?.name || moduleDoc?.code)
        .filter(Boolean);

      return {
        ...enrollment,
        course,
        student,
        studyId: metadata.studyId || fallbackStudyId,
        studyName: metadata.studyName || fallbackStudy?.name || 'Onbekende studie',
        yearId: metadata.yearId || '',
        yearLabel: metadata.yearLabel || `Studiejaar ${Number(enrollment.currentStudyYear || 1)}`,
        yearNumber: metadata.yearNumber || null,
        currentStudyYear: Number(enrollment.currentStudyYear || 1),
        studyYearMode: enrollment.studyYearMode || 'auto',
        moduleId: metadata.moduleId || '',
        moduleName: courseModuleNames.length > 0 ? courseModuleNames.join(', ') : (metadata.moduleName || 'Geen module gekoppeld'),
        courseLocation: course?.location || metadata.courseLocation || '',
        studentLocation: student?.location || ''
      };
    });
  }, [courseMetadataById, data.enrollments, data.students, getCourseById, studies]);

  const isWithinPeriod = (value) => {
    if (periodFilter === 'all' && !selectedMonth) return true;
    if (!value) return false;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;

    if (selectedMonth) {
      const [year, month] = selectedMonth.split('-').map(Number);
      if (date.getFullYear() !== year || date.getMonth() !== month - 1) {
        return false;
      }
    }

    if (periodFilter === 'all') return true;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (periodFilter === 'week') {
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfToday.getDate() - 6);
      return date >= startOfWeek;
    }

    if (periodFilter === 'month') {
      return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
    }

    if (periodFilter === 'year') {
      return date.getFullYear() === now.getFullYear();
    }

    return true;
  };

  const matchesSearch = (enrollment) => {
    const haystack = [
      enrollment.student?.name,
      enrollment.student?.email,
      enrollment.studyName,
      enrollment.yearLabel,
      enrollment.moduleName,
      enrollment.course?.name,
      enrollment.course?.code
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(searchTerm.trim().toLowerCase());
  };

  const matchesSelectedLocation = (enrollment) => {
    if (!selectedLocation || selectedLocation === 'all') {
      return true;
    }

    const normalizedLocation = String(selectedLocation).toLowerCase();
    return String(enrollment.courseLocation || enrollment.studentLocation || '').toLowerCase() === normalizedLocation;
  };

  const studentsForEnrollModal = useMemo(() => {
    if (!enrollModalStudy) return data.students;
    const cohortIds = new Set(
      (data.cohorts || []).filter((c) =>
        (c.studies || []).some((s) => String(s.id || s._id || s) === enrollModalStudy)
      ).map((c) => String(c.id || c._id))
    );
    return data.students.filter((s) =>
      cohortIds.has(String(s.cohort?.id || s.cohort?._id || s.cohort || ''))
    );
  }, [data.students, data.cohorts, enrollModalStudy]);

  const filteredStudentsForEnrollModal = useMemo(() => {
    const query = enrollStudentSearch.trim().toLowerCase();
    if (!query) return studentsForEnrollModal;
    return studentsForEnrollModal.filter((student) => {
      const haystack = `${student.name || ''} ${student.email || ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [studentsForEnrollModal, enrollStudentSearch]);

  const coursesForEnrollModal = useMemo(() => {
    if (!enrollModalStudy) return allCourses;
    return allCourses.filter((c) => {
      const meta = courseMetadataById.get(String(c.id));
      return meta && meta.studyId === enrollModalStudy;
    });
  }, [allCourses, courseMetadataById, enrollModalStudy]);

  const cohortsForBulkEnroll = useMemo(() => {
    return (data.cohorts || []).filter((cohort) => {
      if (bulkStudy && !(cohort.studies || []).some((study) => String(study.id || study._id || study) === String(bulkStudy))) {
        return false;
      }
      if (selectedLocation && selectedLocation !== 'all') {
        return String(cohort.location || '').toLowerCase() === String(selectedLocation).toLowerCase();
      }
      return true;
    });
  }, [data.cohorts, bulkStudy, selectedLocation]);

  const coursesForBulkEnroll = useMemo(() => {
    return allCourses.filter((course) => {
      if (bulkStudy && String(course.studyId) !== String(bulkStudy)) {
        return false;
      }
      if (bulkYear && String(course.yearId) !== String(bulkYear)) {
        return false;
      }
      return true;
    });
  }, [allCourses, bulkStudy, bulkYear]);

  const yearsForBulkEnroll = useMemo(() => {
    const candidate = bulkStudy
      ? courseMetadata.filter((entry) => String(entry.studyId) === String(bulkStudy))
      : courseMetadata;

    return Array.from(new Map(candidate.map((entry) => [entry.yearId, entry])).values())
      .sort((left, right) => Number(left.yearNumber || 0) - Number(right.yearNumber || 0));
  }, [bulkStudy, courseMetadata]);

  const studentsForBulkEnroll = useMemo(() => {
    const selectedCohort = (data.cohorts || []).find((cohort) => String(cohort.id) === String(bulkCohort));
    if (!selectedCohort) return [];

    const cohortStudentIds = new Set((selectedCohort.studentIds || []).map((id) => String(id)));
    const query = bulkStudentSearch.trim().toLowerCase();

    return (data.students || []).filter((student) => {
      if (!cohortStudentIds.has(String(student.id))) {
        return false;
      }
      if (!query) {
        return true;
      }
      return `${student.name || ''} ${student.email || ''}`.toLowerCase().includes(query);
    });
  }, [data.cohorts, data.students, bulkCohort, bulkStudentSearch]);

  const handleEnroll = (e) => {
    e.preventDefault();
    if (selectedCourse && selectedStudent) {
      enrollStudent(selectedStudent, selectedCourse);
      setSelectedCourse('');
      setSelectedStudent('');
      setEnrollModalStudy('');
      setEnrollStudentSearch('');
      setShowEnrollModal(false);
    }
  };

  const handleBulkEnroll = async (e) => {
    e.preventDefault();
    if (!bulkCourse || studentsForBulkEnroll.length === 0) return;

    try {
      const result = await bulkEnrollStudents(
        studentsForBulkEnroll.map((student) => student.id),
        bulkCourse
      );

      if (result?.errors?.length) {
        window.alert(`${result.enrolled || 0} studenten toegewezen. ${result.errors.length} overgeslagen.`);
      }

      setShowBulkEnrollModal(false);
      setBulkStudy('');
      setBulkYear('');
      setBulkCohort('');
      setBulkCourse('');
      setBulkStudentSearch('');
    } catch (error) {
      window.alert(error.message || 'Bulk toewijzing mislukt');
    }
  };

  const handleUnenroll = (enrollmentId) => {
    if (window.confirm('Weet u zeker dat u deze student wilt uitschrijven?')) {
      unenrollStudent(enrollmentId);
    }
  };

  const openEditStatus = (enrollment) => {
    setEditingEnrollment(enrollment);
    setEditingStatus(enrollment.status || 'active');
    setEditingStudyYearMode(enrollment.studyYearMode || 'auto');
    setEditingCurrentStudyYear(String(enrollment.currentStudyYear || 1));
  };

  const editableStudyYearOptions = useMemo(() => {
    if (!editingEnrollment?.studyId) return [];
    const study = studies.find((item) => String(item.id || item._id) === String(editingEnrollment.studyId));
    if (!study) return [];

    const yearNumbers = (study.studyYears || [])
      .map((year) => Number(year.yearNumber || year.year || 0))
      .filter((yearNumber) => Number.isFinite(yearNumber) && yearNumber > 0);

    return Array.from(new Set(yearNumbers)).sort((a, b) => a - b);
  }, [editingEnrollment?.studyId, studies]);

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    if (!editingEnrollment) return;

    const payload = {
      status: editingStatus,
      studyYearMode: editingStudyYearMode
    };

    if (editingStudyYearMode === 'manual') {
      payload.currentStudyYear = Number(editingCurrentStudyYear || 1);
    } else {
      payload.recalculateStudyYear = true;
    }

    await updateEnrollment(editingEnrollment.id, payload);
    setEditingEnrollment(null);
  };

  const enrollments = assignmentRows.filter((enrollment) => {
    if (!matchesSelectedLocation(enrollment)) return false;
    if (!matchesSearch(enrollment)) return false;
    if (!isWithinPeriod(enrollment.enrollmentDate)) return false;
    if (selectedStudy && enrollment.studyId !== selectedStudy) return false;
    if (selectedYear && enrollment.yearId !== selectedYear) return false;
    if (selectedCourseFilter && String(enrollment.courseId) !== String(selectedCourseFilter)) return false;
    return true;
  });

  const groupedEnrollments = enrollments.reduce((accumulator, enrollment) => {
    const studyKey = enrollment.studyId || 'unknown-study';
    if (!accumulator[studyKey]) {
      accumulator[studyKey] = {
        studyId: studyKey,
        studyName: enrollment.studyName,
        years: {}
      };
    }

    const yearKey = enrollment.yearId || 'unknown-year';
    if (!accumulator[studyKey].years[yearKey]) {
      accumulator[studyKey].years[yearKey] = {
        yearId: yearKey,
        yearLabel: enrollment.yearLabel,
        yearNumber: enrollment.yearNumber,
        rows: []
      };
    }

    accumulator[studyKey].years[yearKey].rows.push(enrollment);
    return accumulator;
  }, {});

  const groupedSections = Object.values(groupedEnrollments)
    .sort((left, right) => left.studyName.localeCompare(right.studyName, 'nl'))
    .map((studyGroup) => ({
      ...studyGroup,
      years: Object.values(studyGroup.years).sort((left, right) => {
        const leftYear = Number(left.yearNumber || Number.MAX_SAFE_INTEGER);
        const rightYear = Number(right.yearNumber || Number.MAX_SAFE_INTEGER);
        if (leftYear !== rightYear) {
          return leftYear - rightYear;
        }
        return left.yearLabel.localeCompare(right.yearLabel, 'nl');
      })
    }));

  const availableYears = selectedStudy
    ? courseMetadata.filter((entry) => entry.studyId === selectedStudy)
    : courseMetadata;

  const availableCourses = allCourses.filter((entry) => {
    const metadata = courseMetadataById.get(String(entry.id));
    if (!metadata) return true;
    if (selectedStudy && metadata.studyId !== selectedStudy) return false;
    if (selectedYear && metadata.yearId !== selectedYear) return false;
    return true;
  });

  return (
    <div className="enrollments-page">
      <div className="page-header">
        <div>
          <h1>{t('admin.courseAssignments')}</h1>
          <p className="page-subtitle">{t('admin.assignmentGroupsHint')}</p>
        </div>
        <div className="header-action-row">
          <button type="button" className="btn-secondary" onClick={() => setShowBulkEnrollModal(true)}>
            Cohort aan vak koppelen
          </button>
          <button type="button" className="btn-primary" onClick={() => setShowEnrollModal(true)}>
            {t('admin.enrollStudent')}
          </button>
        </div>
      </div>

      <div className="enrollment-filter-toolbar">
        <input
          type="search"
          className="enrollment-search-input"
          placeholder="Zoek op naam, e-mail of vak"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select className="enrollment-select-input" value={selectedStudy} onChange={(e) => {
          setSelectedStudy(e.target.value);
          setSelectedYear('');
          setSelectedCourseFilter('');
        }}>
          <option value="">Alle studies</option>
          {studiesWithAssignments.map((study) => (
            <option key={study.id} value={study.id}>{getStudyOptionLabel(study)}</option>
          ))}
        </select>
        <select className="enrollment-select-input" value={selectedYear} onChange={(e) => {
          setSelectedYear(e.target.value);
          setSelectedCourseFilter('');
        }}>
          <option value="">Alle studiejaren</option>
          {Array.from(new Map(availableYears.map((entry) => [entry.yearId, entry]))).values().map((entry) => (
            <option key={entry.yearId} value={entry.yearId}>{entry.yearLabel}</option>
          ))}
        </select>
        <select className="enrollment-select-input" value={selectedCourseFilter} onChange={(e) => {
          setSelectedCourseFilter(e.target.value);
        }}>
          <option value="">Alle vakken</option>
          {availableCourses.map((course) => (
            <option key={course.id} value={course.id}>{course.code} - {course.name}</option>
          ))}
        </select>
        <input
          type="month"
          className="enrollment-month-input"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        />
      </div>

      <div className="filter-button-group enrollment-period-buttons">
        {periodOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`filter-pill ${periodFilter === option.value ? 'active' : ''}`}
            onClick={() => setPeriodFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <Modal isOpen={showEnrollModal} onClose={() => { setShowEnrollModal(false); setEnrollModalStudy(''); setSelectedStudent(''); setSelectedCourse(''); setEnrollStudentSearch(''); }} title={t('admin.enrollStudent')}>
        <form onSubmit={handleEnroll} className="enroll-form">
          <select
            value={enrollModalStudy}
            onChange={(e) => { setEnrollModalStudy(e.target.value); setSelectedStudent(''); setSelectedCourse(''); setEnrollStudentSearch(''); }}
            className="form-select"
          >
            <option value="">-- Alle studies --</option>
            {studiesWithAssignments.map((study) => (
              <option key={study.id} value={study.id}>{getStudyOptionLabel(study)}</option>
            ))}
          </select>

          <input
            type="search"
            value={enrollStudentSearch}
            onChange={(e) => setEnrollStudentSearch(e.target.value)}
            placeholder="Typ studentnaam of e-mail..."
            className="form-select"
          />

          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            className="form-select"
          >
            <option value="">-- {t('enrollment.student')} --</option>
            {filteredStudentsForEnrollModal.map(student => (
              <option key={student.id} value={student.id}>
                {student.name} ({student.email})
              </option>
            ))}
          </select>

          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="form-select"
          >
            <option value="">-- {t('enrollment.course')} --</option>
            {coursesForEnrollModal.map(course => (
              <option key={course.id} value={course.id}>
                {course.code} - {course.name} ({course.studyName} / {course.yearLabel})
              </option>
            ))}
          </select>

          {enrollModalStudy && filteredStudentsForEnrollModal.length === 0 && (
            <p style={{ color: '#999', fontSize: '0.85rem', margin: '4px 0' }}>
              Geen studenten gevonden voor deze studie en zoekterm.
            </p>
          )}

          <button type="submit" className="btn-primary" disabled={!selectedStudent || !selectedCourse}>
            {t('enrollment.add')}
          </button>
        </form>
      </Modal>

      <Modal isOpen={showBulkEnrollModal} onClose={() => { setShowBulkEnrollModal(false); setBulkStudy(''); setBulkYear(''); setBulkCohort(''); setBulkCourse(''); setBulkStudentSearch(''); }} title="Cohort aan vak koppelen">
        <form onSubmit={handleBulkEnroll} className="bulk-enroll-form">
          <select
            value={bulkStudy}
            onChange={(e) => {
              setBulkStudy(e.target.value);
              setBulkYear('');
              setBulkCohort('');
              setBulkCourse('');
              setBulkStudentSearch('');
            }}
            className="form-select"
          >
            <option value="">-- Kies studie --</option>
            {studies.map((study) => (
              <option key={study.id} value={study.id}>{getStudyOptionLabel(study)}</option>
            ))}
          </select>

          <select
            value={bulkYear}
            onChange={(e) => {
              setBulkYear(e.target.value);
              setBulkCourse('');
            }}
            className="form-select"
            disabled={!bulkStudy}
          >
            <option value="">-- Kies studiejaar --</option>
            {yearsForBulkEnroll.map((yearEntry) => (
              <option key={yearEntry.yearId} value={yearEntry.yearId}>{yearEntry.yearLabel}</option>
            ))}
          </select>

          <select
            value={bulkCohort}
            onChange={(e) => {
              setBulkCohort(e.target.value);
              setBulkStudentSearch('');
            }}
            className="form-select"
            disabled={!bulkStudy}
          >
            <option value="">-- Kies cohort --</option>
            {cohortsForBulkEnroll.map((cohort) => (
              <option key={cohort.id} value={cohort.id}>{cohort.name} ({cohort.studentCount || 0} studenten)</option>
            ))}
          </select>

          <select
            value={bulkCourse}
            onChange={(e) => setBulkCourse(e.target.value)}
            className="form-select"
            disabled={!bulkStudy || !bulkYear}
          >
            <option value="">-- Kies vak --</option>
            {coursesForBulkEnroll.map((course) => (
              <option key={course.id} value={course.id}>{course.code} - {course.name} ({course.yearLabel})</option>
            ))}
          </select>

          <input
            type="search"
            value={bulkStudentSearch}
            onChange={(e) => setBulkStudentSearch(e.target.value)}
            placeholder="Filter studenten in cohort op naam of e-mail..."
            className="form-select"
            disabled={!bulkCohort}
          />

          <div className="bulk-enroll-preview">
            <strong>{studentsForBulkEnroll.length}</strong> student{studentsForBulkEnroll.length === 1 ? '' : 'en'} geselecteerd voor toewijzing.
          </div>

          {studentsForBulkEnroll.length > 0 && (
            <div className="bulk-student-list">
              {studentsForBulkEnroll.slice(0, 12).map((student) => (
                <span key={student.id} className="registration-course-chip">{student.name}</span>
              ))}
              {studentsForBulkEnroll.length > 12 && (
                <span className="registration-course-chip muted">+{studentsForBulkEnroll.length - 12} meer</span>
              )}
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={!bulkStudy || !bulkCohort || !bulkCourse || studentsForBulkEnroll.length === 0}>
              Koppel cohort aan vak
            </button>
            <button type="button" className="btn-secondary" onClick={() => { setShowBulkEnrollModal(false); setBulkStudy(''); setBulkYear(''); setBulkCohort(''); setBulkCourse(''); setBulkStudentSearch(''); }}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </Modal>

      <div className="assignment-groups">
        {groupedSections.map((studyGroup) => (
          <section key={studyGroup.studyId} className="assignment-section">
            <div className="assignment-section-header">
              <h2>{studyGroup.studyName}</h2>
              <span>{studyGroup.years.reduce((count, year) => count + year.rows.length, 0)} toewijzingen</span>
            </div>

            {studyGroup.years.map((yearGroup) => (
              <div key={yearGroup.yearId} className="assignment-year-card">
                <div className="assignment-year-header">
                  <h3>{yearGroup.yearLabel}</h3>
                  <span>{yearGroup.rows.length} toewijzingen</span>
                </div>
                <div className="enrollments-list">
                  <table className="enrollments-table">
                    <thead>
                      <tr>
                        <th>{t('enrollment.student')}</th>
                        <th>Automatische modules</th>
                        <th>{t('enrollment.course')}</th>
                        <th>Huidig studiejaar</th>
                        <th>{t('enrollment.enrolledOn')}</th>
                        <th>{t('common.status')}</th>
                        <th>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearGroup.rows.map((enrollment) => (
                        <tr key={enrollment.id}>
                          <td>
                            {enrollment.student?.name || 'Onbekend'}
                            <br />
                            <small>{enrollment.student?.email}</small>
                          </td>
                          <td>{enrollment.moduleName}</td>
                          <td>
                            {enrollment.course?.name || 'Onbekend'}
                            <br />
                            <small>{enrollment.course?.code} · {enrollment.yearLabel}</small>
                          </td>
                          <td>
                            <strong>Jaar {enrollment.currentStudyYear || 1}</strong>
                            <br />
                            <small>{enrollment.studyYearMode === 'manual' ? 'Handmatig' : 'Automatisch'}</small>
                          </td>
                          <td>{enrollment.enrolledOn || '-'}</td>
                          <td>
                            <span className={`status-badge ${enrollment.status}`}>
                              {t(`enrollment.status.${enrollment.status}`, t(`enrollment.${enrollment.status}`, enrollment.status))}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-secondary"
                              onClick={() => openEditStatus(enrollment)}
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => handleUnenroll(enrollment.id)}
                            >
                              {t('common.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </section>
        ))}

        {groupedSections.length === 0 && (
          <p className="no-data">{t('common.noData')}</p>
        )}
      </div>

      <Modal isOpen={!!editingEnrollment} onClose={() => setEditingEnrollment(null)} title={t('common.edit')} size="sm">
        <form onSubmit={handleUpdateStatus}>
          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="enrollment-status">{t('common.status')}</label>
            <select
              id="enrollment-status"
              value={editingStatus}
              onChange={(e) => setEditingStatus(e.target.value)}
              className="form-select"
            >
              <option value="active">{t('enrollment.status.active')}</option>
              <option value="completed">{t('enrollment.status.completed')}</option>
              <option value="cancelled">{t('enrollment.status.cancelled')}</option>
            </select>
          </div>

          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="study-year-mode">Studiejaar modus</label>
            <select
              id="study-year-mode"
              value={editingStudyYearMode}
              onChange={(e) => setEditingStudyYearMode(e.target.value)}
              className="form-select"
            >
              <option value="auto">Automatisch (op basis van behaalde vakken)</option>
              <option value="manual">Handmatig instellen</option>
            </select>
          </div>

          {editingStudyYearMode === 'manual' && (
            <div className="form-row" style={{ marginBottom: '12px' }}>
              <label htmlFor="current-study-year">Huidig studiejaar</label>
              <select
                id="current-study-year"
                value={editingCurrentStudyYear}
                onChange={(e) => setEditingCurrentStudyYear(e.target.value)}
                className="form-select"
              >
                {(editableStudyYearOptions.length > 0 ? editableStudyYearOptions : [1, 2, 3, 4]).map((yearNumber) => (
                  <option key={yearNumber} value={String(yearNumber)}>Jaar {yearNumber}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('common.save')}</button>
            <button type="button" className="btn-secondary" onClick={() => setEditingEnrollment(null)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Enrollments;
